<template>
  <div class="ticket-form">

    <!-- ========== CARD 1: TICKET DETAILS ========== -->
    <section class="card">
      <div class="card-header">
        <h3 class="card-title">Ticket Details</h3>
      </div>
      <div class="card-body">
        <div class="form-grid">
          <!-- Status (view only) -->
          <div class="form-row">
            <label class="form-label">Status</label>
            <div class="form-field">
              <div class="form-display">
                <StatusBadge v-if="!createMode" :status="ticketStatus" />
                <StatusBadge v-else status="In Progress" />
              </div>
            </div>
          </div>

          <!-- Patient -->
          <div class="form-row">
            <label class="form-label">Patient</label>
            <div class="form-field">
              <div class="form-editor" v-if="createMode">
                <PatientSelectInput :modelValue="modelValue.patientId"
                  @update:modelValue="updateField('patientId', $event)" placeholder="Select patient" />
              </div>
              <div class="form-display" v-else>
                <router-link v-if="modelValue.patientId" :to="`/patients/${modelValue.patientId}`" class="patient-link">
                  <IconCircle :initials="getPatientInitials()" :size="22"
                    bgColor="var(--icon-patient-bg)" color="var(--icon-patient-color)" />
                  <span class="patient-link-name">{{ getPatientName() }}</span>
                </router-link>
                <span v-else class="no-data">No data</span>
              </div>
            </div>
          </div>

          <!-- Counselor -->
          <div class="form-row">
            <label class="form-label">Sales Counselor</label>
            <div class="form-field">
              <div class="form-editor" v-if="createMode || editMode">
                <UserSelectInput :modelValue="modelValue.counselorId"
                  @update:modelValue="updateField('counselorId', $event)" placeholder="Select counselor" />
              </div>
              <div class="form-display" v-else>
                <span v-if="modelValue.counselorId" class="counselor-display">
                  <IconCircle :initials="getCounselorInitial()" :size="22"
                    bgColor="#6366f1" color="#fff" />
                  <span class="counselor-display-name">{{ getCounselorName() }}</span>
                </span>
                <span v-else class="no-data">No data</span>
              </div>
            </div>
          </div>

          <!-- Date -->
          <div class="form-row">
            <label class="form-label">Ticket Date</label>
            <div class="form-field">
              <div class="form-editor" v-if="createMode || editMode">
                <DateInput :modelValue="modelValue.dateTicketStart"
                  @update:modelValue="updateField('dateTicketStart', $event)" placeholder="MM/DD/YYYY" />
              </div>
              <div class="form-display" v-else>
                <span :class="{ 'no-data': !modelValue.dateTicketStart }">
                  {{ formatDate(modelValue.dateTicketStart) || 'No data' }}
                </span>
              </div>
            </div>
          </div>

          <!-- Ticket Items (inside form-grid so spacing matches) -->
          <div class="form-row form-row-items">
            <label class="form-label">Ticket Items</label>
            <div class="form-field">
              <div class="products-table-scroll">
              <div class="product-row product-row-head">
                <div class="col-product">Product</div>
                <div class="col-duration">Duration</div>
                <div class="col-quantity">Qty</div>
                <div class="col-price">Price</div>
                <div class="col-allocation">Allocation</div>
                <div class="col-action"></div>
              </div>

            <div v-for="(item, index) in displayItems" :key="getItemId(item, index)"
              class="product-row"
              :class="{ 'product-row-add': createMode && !item.productId }">
              <div class="col-product">
                <ProductSelectInput v-if="createMode" :modelValue="item.productId"
                  @update:modelValue="handleProductSelect(index, $event)" />
                <ProductSelectInput v-else-if="editMode && item.productId" :modelValue="item.productId"
                  @update:modelValue="handleProductSelectEdit(index, $event)" />
                <div v-else-if="item.productId" class="product-name">
                  {{ getProductName(item) }}
                </div>
                <span v-else class="no-data">N/A</span>
              </div>
              <div class="col-duration">
                <SelectInput v-if="createMode && item.productId" :modelValue="item.durationMonths"
                  @update:modelValue="updateItem(index, 'durationMonths', $event)"
                  :options="durationOptions" placeholder="Select..." />
                <SelectInput v-else-if="editMode && item.productId" :modelValue="item.durationMonths"
                  @update:modelValue="updateItemSimple(index, 'durationMonths', $event)"
                  :options="durationOptions" placeholder="Select..." />
                <span v-else-if="item.productId" class="cell-text">{{ formatDuration(item.durationMonths) }}</span>
                <span v-else class="cell-placeholder">—</span>
              </div>
              <div class="col-quantity">
                <NumericalInput v-if="createMode && item.productId" :modelValue="item.quantity"
                  @update:modelValue="updateItem(index, 'quantity', $event)" />
                <NumericalInput v-else-if="editMode && item.productId" :modelValue="item.quantity"
                  @update:modelValue="updateItemSimple(index, 'quantity', $event)" />
                <span v-else-if="item.productId" class="cell-text">{{ item.quantity || '—' }}</span>
                <span v-else class="cell-placeholder">—</span>
              </div>
              <div class="col-price">
                <MonetaryInput v-if="createMode && item.productId" :modelValue="item.amountCents"
                  @update:modelValue="updateItem(index, 'amountCents', $event)" />
                <div v-else-if="editMode && item.productId" class="locked-field" title="Total cost cannot be changed">
                  <span>{{ formatCurrency(item.amountCents) }}</span>
                  <LockIcon :size="14" class="lock-icon" />
                </div>
                <span v-else-if="item.productId" class="cell-text cell-text-strong">{{ formatCurrency(item.amountCents) }}</span>
                <span v-else class="cell-placeholder">—</span>
              </div>
              <div class="col-allocation">
                <template v-if="item.productId && item.allowanceEnabled && item.allowanceAmount && item.quantity">
                  <span v-if="!createMode && !editMode" class="allocation-text">
                    <strong :class="{ 'allocation-over': allocationUsage(item).remaining < 0 }">{{ allocationUsage(item).remaining }}</strong>/{{ allocationUsage(item).total }}
                    {{ item.allowanceUnit || 'units' }} remaining
                  </span>
                  <span v-else class="allocation-text">
                    <strong>{{ item.allowanceAmount * item.quantity }}</strong>
                    {{ item.allowanceUnit || 'units' }}
                  </span>
                </template>
                <span v-else class="cell-placeholder">—</span>
              </div>
              <div class="col-action">
                <button v-if="createMode && item.productId" @click="removeItem(index)"
                  class="button icon-btn" title="Remove item">
                  <DeleteIcon :size="18" />
                </button>
                <button v-else-if="!createMode && !editMode && item.productId && item.allowanceEnabled"
                  @click="$emit('update-allocation', item)"
                  class="button button-small update-btn">
                  Update
                </button>
              </div>
            </div>

            <div v-if="!createMode && ticketItems.length === 0" class="product-empty">
              No products on this ticket
            </div>

            <div class="product-row product-row-foot">
              <div class="col-product">Total</div>
              <div class="col-duration"></div>
              <div class="col-quantity"></div>
              <div class="col-price foot-amount">{{ formatCurrency(amountTotal) }}</div>
              <div class="col-allocation"></div>
              <div class="col-action"></div>
            </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ========== CARD 2: PAYMENT PLAN ========== -->
    <section class="card">
      <div class="card-header">
        <h3 class="card-title">Payment Plan</h3>
      </div>
      <div class="card-body">
        <div class="form-grid">
          <div class="form-row">
            <label class="form-label">Total Amount</label>
            <div class="form-field">
              <div class="form-display">
                <span class="balance-value">{{ formatCurrency(createMode ? amountTotal : modelValue.amountCentsTotal) }}</span>
              </div>
            </div>
          </div>
          <div class="form-row">
            <label class="form-label">Initial Payment</label>
            <div class="form-field">
              <div class="form-editor" v-if="createMode">
                <MonetaryInput :modelValue="modelValue.amountDueToday"
                  @update:modelValue="handleInitialPaymentChange" />
              </div>
              <div class="form-display" v-else>
                <span>{{ formatCurrency(modelValue.amountDueToday) }}</span>
              </div>
            </div>
          </div>
          <div class="form-row">
            <label class="form-label">Payment Plan Amount</label>
            <div class="form-field">
              <div class="form-editor" v-if="createMode">
                <MonetaryInput :modelValue="paymentPlanAmount"
                  @update:modelValue="handlePaymentPlanAmountChange" />
              </div>
              <div class="form-display" v-else>
                <span>{{ formatCurrency(paymentPlanAmount) }}</span>
              </div>
            </div>
          </div>
          <div class="form-row">
            <label class="form-label">Payment Plan Term</label>
            <div class="form-field">
              <div class="form-editor" v-if="createMode">
                <SelectInput :modelValue="modelValue.paymentPlanMonths"
                  @update:modelValue="updateField('paymentPlanMonths', $event)"
                  :options="paymentPlanOptions" placeholder="Select term" />
              </div>
              <div class="form-display" v-else>
                <span>{{ formatPaymentPlan(modelValue.paymentPlanMonths) }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ========== CARD 3: PAYMENT ACTIVITY (view/edit only) ========== -->
    <section v-if="!createMode" class="card">
      <div class="card-header">
        <h3 class="card-title">Payment Activity</h3>
      </div>
      <div class="card-body">
        <div class="form-grid">
          <div class="form-row">
            <label class="form-label">Amount Paid</label>
            <div class="form-field">
              <div class="form-display">
                <span>{{ formatCurrency(modelValue.amountCentsPaid) }}</span>
              </div>
            </div>
          </div>
          <div class="form-row">
            <label class="form-label">Amount Refunded</label>
            <div class="form-field">
              <div class="form-display">
                <span>{{ formatCurrency(modelValue.amountCentsRefunded) }}</span>
              </div>
            </div>
          </div>
          <div class="form-row">
            <label class="form-label">Amount Cancelled</label>
            <div class="form-field">
              <div class="form-display">
                <span>{{ formatCurrency(modelValue.amountCentsCancelled) }}</span>
              </div>
            </div>
          </div>
          <div class="form-row">
            <label class="form-label">Balance</label>
            <div class="form-field">
              <div class="form-display">
                <span class="balance-value">{{ formatCurrency(modelValue.amountCentsBalance) }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

  </div>
</template>

<script>
import DateInput from '../inputs/DateInput.vue';
import PatientSelectInput from '../inputs/PatientSelectInput.vue';
import UserSelectInput from '../inputs/UserSelectInput.vue';
import ProductSelectInput from '../inputs/ProductSelectInput.vue';
import NumericalInput from '../inputs/NumericalInput.vue';
import MonetaryInput from '../inputs/MonetaryInput.vue';
import SelectInput from '../inputs/SelectInput.vue';
import IconCircle from '../common/IconCircle.vue';
import StatusBadge from '../common/StatusBadge.vue';
import DeleteIcon from 'vue-material-design-icons/Delete.vue';
import LockIcon from 'vue-material-design-icons/Lock.vue';
import * as formatUtils from '@/utils/formatters';
import { calculateTicketStatus } from '@/utils/formatters';
import { addMonths } from '@/utils/dateUtils';
import * as api from '@/api';
import { queryClient } from '@/libs/queryClient';

export default {
  name: 'CardTicketForm',
  props: {
    modelValue: { type: Object, required: true },
    createMode: { type: Boolean, default: false },
    editMode: { type: Boolean, default: false }
  },
  emits: ['update:modelValue', 'update-allocation'],
  components: {
    DateInput, PatientSelectInput, UserSelectInput, ProductSelectInput,
    NumericalInput, MonetaryInput, SelectInput, IconCircle, StatusBadge,
    DeleteIcon, LockIcon,
  },
  data() {
    return {
      distributionEventsByItem: {},
      isLoadingAllocations: false
    };
  },
  created() {
    this.loadAllocationUsage();
  },
  watch: {
    'modelValue.ticketId'() {
      this.loadAllocationUsage();
    }
  },
  computed: {
    durationOptions() {
      return [
        { value: 0, label: 'None' },
        { value: 1, label: '1 mo' }, { value: 2, label: '2 mo' }, { value: 3, label: '3 mo' },
        { value: 4, label: '4 mo' }, { value: 5, label: '5 mo' }, { value: 6, label: '6 mo' },
        { value: 7, label: '7 mo' }, { value: 8, label: '8 mo' }, { value: 9, label: '9 mo' },
        { value: 10, label: '10 mo' }, { value: 11, label: '11 mo' }, { value: 12, label: '12 mo' }
      ];
    },
    ticketItems() {
      return this.modelValue.ticketItems || [];
    },
    displayItems() {
      if (this.createMode) {
        return [...this.ticketItems, { productId: null, durationMonths: null, quantity: null, amountCents: null }];
      }
      return this.ticketItems;
    },
    amountTotal() {
      return this.ticketItems.reduce((sum, item) => sum + (item.amountCents || 0), 0);
    },
    paymentPlanAmount() {
      const initialPayment = this.modelValue.amountDueToday || 0;
      return Math.max(0, this.amountTotal - initialPayment);
    },
    paymentPlanOptions() {
      const options = [];
      const planAmount = this.paymentPlanAmount;
      for (let months = 1; months <= 12; months++) {
        const monthlyAmount = planAmount / months;
        const monthlyFormatted = this.formatCurrency(monthlyAmount);
        const label = `${months} ${months === 1 ? 'month' : 'months'} (${monthlyFormatted}/month)`;
        options.push({ value: months, label });
      }
      return options;
    },
    ticketStatus() {
      return calculateTicketStatus(this.modelValue);
    },
    generatedReceivables() {
      const receivables = [];
      const amountDueToday = this.modelValue.amountDueToday || 0;
      const paymentPlanMonths = this.modelValue.paymentPlanMonths || 0;
      const dateTicketStart = this.modelValue.dateTicketStart;

      if (!dateTicketStart) return receivables;

      if (amountDueToday > 0) {
        receivables.push({ dateDue: dateTicketStart, amountCents: amountDueToday });
      }

      if (this.paymentPlanAmount > 0 && paymentPlanMonths > 0) {
        const monthlyAmount = Math.floor(this.paymentPlanAmount / paymentPlanMonths);
        let amountLeft = this.paymentPlanAmount;

        for (let i = 1; i <= paymentPlanMonths; i++) {
          const isLast = (i === paymentPlanMonths);
          const amount = isLast ? amountLeft : monthlyAmount;
          const dueDate = this.calculateDueDateForMonth(dateTicketStart, i);
          receivables.push({ dateDue: dueDate, amountCents: amount });
          amountLeft -= amount;
        }
      }

      return receivables;
    }
  },
  methods: {
    updateField(fieldName, value) {
      this.$emit('update:modelValue', { ...this.modelValue, [fieldName]: value });
    },
    handleInitialPaymentChange(value) {
      this.updateField('amountDueToday', value);
    },
    handlePaymentPlanAmountChange(value) {
      const initialPayment = Math.max(0, this.amountTotal - value);
      this.updateField('amountDueToday', initialPayment);
    },
    formatDate(date) {
      return formatUtils.formatDate(date);
    },
    formatCurrency(cents) {
      if (cents === null || cents === undefined) return 'N/A';
      const dollars = cents / 100;
      return '$' + dollars.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    },
    formatPaymentPlan(months) {
      if (!months) return 'N/A';
      const monthlyAmount = this.paymentPlanAmount / months;
      const monthlyFormatted = this.formatCurrency(monthlyAmount);
      return `${months} mo · ${monthlyFormatted}/mo`;
    },
    calculateDueDateForMonth(startDate, monthOffset) {
      return addMonths(startDate, monthOffset);
    },
    handleProductSelect(index, productId) {
      this.updateItem(index, 'productId', productId);
    },
    async handleProductSelectEdit(index, productId) {
      let newItems = [...this.ticketItems];
      const originalAmount = newItems[index].amountCents;

      try {
        const product = await queryClient.fetchQuery({
          queryKey: ['product', productId],
          queryFn: () => api.fetchProductById(productId)
        });

        newItems[index] = {
          ...newItems[index],
          productId,
          productName: product.name,
          productType: product.productType,
          quantity: newItems[index].quantity || product.defaultQuantity || 1,
          durationMonths: newItems[index].durationMonths || product.defaultDurationMonths || 1,
          amountCents: originalAmount,
          allowanceEnabled: product.allowanceEnabled,
          allowanceUnit: product.allowanceUnit,
          allowanceAmount: product.allowanceAmount
        };
      } catch (error) {
        console.error('Failed to fetch product:', error);
        newItems[index] = { ...newItems[index], productId };
      }

      this.updateField('ticketItems', newItems);
    },
    updateItemSimple(index, field, value) {
      let newItems = [...this.ticketItems];
      newItems[index] = { ...newItems[index], [field]: value };
      this.updateField('ticketItems', newItems);
    },
    removeItem(index) {
      const newItems = this.ticketItems.filter((_, i) => i !== index);
      this.updateField('ticketItems', newItems);
    },
    async updateItem(index, field, value) {
      let newItems = [...this.ticketItems];

      if (index === newItems.length) {
        newItems.push({ productId: null, durationMonths: null, quantity: null, amountCents: null });
      }

      if (field === 'productId' && value) {
        try {
          const product = await queryClient.fetchQuery({
            queryKey: ['product', value],
            queryFn: () => api.fetchProductById(value)
          });

          newItems[index] = {
            ...newItems[index],
            productId: value,
            productName: product.name,
            productType: product.productType,
            quantity: product.defaultQuantity ?? 1,
            durationMonths: product.defaultDurationMonths ?? 1,
            amountCents: (product.defaultQuantity ?? 1) * (product.defaultUnitPriceCents ?? 0),
            defaultUnitPriceCents: product.defaultUnitPriceCents,
            allowanceEnabled: product.allowanceEnabled,
            allowanceUnit: product.allowanceUnit,
            allowanceAmount: product.allowanceAmount
          };
        } catch (error) {
          console.error('Failed to fetch product defaults:', error);
          newItems[index] = { ...newItems[index], [field]: value };
        }
      } else if (field === 'quantity') {
        const item = newItems[index];
        newItems[index] = {
          ...item,
          quantity: value,
          amountCents: item.defaultUnitPriceCents ? value * item.defaultUnitPriceCents : item.amountCents
        };
      } else {
        newItems[index] = { ...newItems[index], [field]: value };
      }

      this.updateField('ticketItems', newItems);
    },
    getPatientInitials() {
      const firstName = this.modelValue.patientFirstName || '';
      const lastName = this.modelValue.patientLastName || '';
      return `${firstName[0] || ''}${lastName[0] || ''}`.toUpperCase() || 'U';
    },
    getPatientName() {
      const firstName = this.modelValue.patientFirstName || '';
      const lastName = this.modelValue.patientLastName || '';
      return `${firstName} ${lastName}`.trim() || 'Unknown';
    },
    getCounselorName() {
      return this.modelValue.counselorName?.trim() || 'Unknown';
    },
    getCounselorInitial() {
      const name = this.modelValue.counselorName?.trim() || '';
      const parts = name.split(/\s+/).filter(Boolean);
      if (parts.length === 0) return 'U';
      if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
      return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
    },
    getProductName(item) {
      return item.productName?.trim() || 'Unknown';
    },
    formatDuration(months) {
      if (!months || months === 0) return 'None';
      return `${months} mo`;
    },
    getItemId(item, index) {
      return `${index}_${item.productId}`;
    },
    allocationUsage(item) {
      const total = (item.allowanceAmount || 0) * (item.quantity || 0);
      const events = this.distributionEventsByItem[item.ticketItemId] || [];
      const used = events.reduce((sum, e) => sum + (e.amount || 0), 0);
      return {
        total,
        used,
        remaining: total - used,
        loading: this.isLoadingAllocations
      };
    },
    async loadAllocationUsage() {
      const ticketId = this.modelValue.ticketId;
      if (!ticketId || this.createMode) return;
      this.isLoadingAllocations = true;
      try {
        const response = await queryClient.fetchQuery({
          queryKey: ['distribution-events', 'by-ticket', ticketId],
          queryFn: () => api.fetchDistributionEvents({ ticketId })
        });
        const grouped = {};
        (response.distributionEvents || []).forEach(event => {
          if (!grouped[event.ticketItemId]) grouped[event.ticketItemId] = [];
          grouped[event.ticketItemId].push(event);
        });
        this.distributionEventsByItem = grouped;
      } catch (err) {
        console.error('Failed to load allocation usage:', err);
      } finally {
        this.isLoadingAllocations = false;
      }
    },
    async refreshAllocationUsage() {
      const ticketId = this.modelValue.ticketId;
      if (!ticketId) return;
      queryClient.invalidateQueries({ queryKey: ['distribution-events'] });
      // Allowance remaining is derived from dispensed doses, so drop the ticket
      // list/search cache to reflect the new count
      queryClient.invalidateQueries({ queryKey: ['tickets'] });
      await this.loadAllocationUsage();
    }
  }
}
</script>

<style scoped>
/* ========== ROOT ========== */
.ticket-form {
  display: flex;
  flex-direction: column;
  background: #f5f6f8;
  min-height: 100%;
  width: 100%;
  box-sizing: border-box;
}

.ticket-form *,
.ticket-form *::before,
.ticket-form *::after {
  box-sizing: border-box;
}

/* ========== CARDS ========== */
.card {
  background: #fff;
  width: 100%;
}

.card + .card {
  border-top: 1px solid var(--content-header-stroke);
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  background-color: var(--content-header-bg);
  border-bottom: var(--content-header-border);
}

.card-title {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: #262626;
}

.card-body {
  padding: 16px;
}

/* ========== FORM GRID (matches CardInputForm) ========== */
.form-grid {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.form-row {
  display: flex;
  gap: 16px;
  align-items: start;
}

.form-label {
  font-weight: 500;
  color: #333;
  line-height: 31px;
  flex-basis: 200px;
  flex-shrink: 0;
}

.form-field {
  width: 1px;
  flex: 1;
}

.form-editor {
  max-width: 400px;
}

.form-display {
  max-width: 400px;
  color: #333;
  min-height: 31px;
  display: flex;
  align-items: center;
  padding: 0 2px;
}

.section-heading {
  margin: 0 0 12px 0;
  font-size: 13px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.section-heading-activity {
  margin-top: 24px;
  padding-top: 16px;
  border-top: 1px solid var(--content-header-stroke);
}

.balance-value {
  font-weight: 700;
  color: #262626;
}

/* ========== PATIENT LINK ========== */
.patient-link {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  text-decoration: none;
  color: inherit;
  padding: 2px 8px 2px 2px;
  border-radius: var(--border-radius);
  border: 1px solid transparent;
  transition: background-color 0.15s ease, border-color 0.15s ease;
}

.patient-link:hover {
  background-color: #f3f4f6;
  border-color: #e5e7eb;
}

.patient-link-name {
  font-size: 13px;
  font-weight: 500;
  color: var(--primary-color);
}

.patient-link:hover .patient-link-name {
  text-decoration: underline;
}

.counselor-display {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 2px 2px;
}

.counselor-display-name {
  font-size: 13px;
  font-weight: 500;
  color: #262626;
}

/* ========== PRODUCTS TABLE ========== */
.form-row-items .form-field {
  max-width: none;
}

.products-table-scroll {
  width: 1000px;
  max-width: 100%;
  border: 1px solid var(--content-header-stroke);
  border-radius: var(--border-radius);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06), 0 4px 12px rgba(0, 0, 0, 0.04);
  background: #fff;
  margin-bottom: 8px;
}

.product-row {
  display: grid;
  grid-template-columns:
    minmax(0, 2fr)
    minmax(0, 1fr)
    minmax(0, 0.7fr)
    minmax(0, 0.9fr)
    minmax(0, 1.9fr)
    minmax(0, 1.1fr);
  gap: 0;
  align-items: center;
  padding: 10px 0;
  border-bottom: 1px solid #f3f4f6;
}

.product-row:last-child {
  border-bottom: none;
}

.product-row > div {
  padding: 0 16px;
}

.product-row-head {
  font-size: 14px;
  font-weight: 600;
  color: #262626;
  background-color: var(--content-header-bg);
  border-bottom: var(--content-header-border);
  padding: 0;
  min-height: 50px;
}

.product-row-head > div {
  position: relative;
  height: 50px;
  display: flex;
  align-items: center;
}

.product-row-head > div:not(:last-child)::after {
  content: '';
  position: absolute;
  right: 0;
  top: 25%;
  height: 50%;
  width: 1px;
  background-color: var(--content-header-stroke);
}

.product-row-foot {
  background-color: var(--content-header-bg);
  border-top: 1px solid var(--content-header-stroke);
  border-bottom: none;
  min-height: 44px;
  font-weight: 600;
  color: #262626;
  font-size: 14px;
}

.product-row-foot .foot-amount {
  font-variant-numeric: tabular-nums;
  font-weight: 700;
  color: #262626;
  text-align: right;
}

.product-row-add {
  background: #fcfcfd;
}

.col-product { min-width: 0; }
.col-duration { min-width: 0; }
.col-quantity { min-width: 0; }
.col-price { text-align: right; min-width: 0; }
.col-allocation { min-width: 0; text-align: right; justify-content: flex-end; }
.col-action { text-align: right; min-width: 0; }

.product-row-head .col-price,
.product-row-head .col-allocation,
.product-row-head .col-action {
  justify-content: flex-end;
}

.cell-text {
  font-size: 14px;
  color: #333;
  font-variant-numeric: tabular-nums;
}

.cell-text-strong {
  font-weight: 600;
  color: #262626;
}

.cell-placeholder {
  color: #d1d5db;
  font-size: 14px;
}

.product-name {
  font-weight: 500;
  color: #262626;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 14px;
}

.allocation-text {
  font-size: 14px;
  color: #6b7280;
  font-variant-numeric: tabular-nums;
}

.allocation-text strong {
  color: #262626;
  font-weight: 600;
}

.allocation-over {
  color: #b91c1c !important;
}

.no-data {
  color: #9ca3af;
  font-style: italic;
}

.product-empty {
  text-align: center;
  color: #9ca3af;
  font-style: italic;
  padding: 32px 16px;
  background: #fafbfc;
}

.icon-btn {
  padding: 4px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 31px;
  height: 31px;
}

.update-btn {
  padding: 6px 16px;
  font-size: 13px;
}

.locked-field {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background-color: #f5f5f5;
  padding: 4px 10px;
  border: 1px solid #e0e0e0;
  color: #666;
  cursor: not-allowed;
  border-radius: var(--border-radius);
}

.lock-icon { color: #999; }
</style>
